type FunnelPayloadValue = string | number | boolean | null;
type FunnelPayload = Record<string, FunnelPayloadValue>;

type FunnelWindow = Window &
  typeof globalThis & {
    gtag?: (command: "event", eventName: string, payload: FunnelPayload) => void;
    dataLayer?: Array<Record<string, FunnelPayloadValue>>;
  };

const firedEvents = new Set<string>();

const toNullableNumber = (value: number | string | null | undefined) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const parsed = typeof value === "number" ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const normalizePaymentToken = (paymentToken: string | null | undefined) => {
  if (!paymentToken) {
    return null;
  }

  const symbolMatch = paymentToken.trim().match(/\b(USDT|USDC)\b/i);
  if (symbolMatch) {
    return symbolMatch[1].toUpperCase();
  }

  const trimmedToken = paymentToken.trim();
  return trimmedToken ? trimmedToken.toUpperCase() : null;
};

const getWindow = () => {
  if (typeof window === "undefined") {
    return null;
  }

  return window as FunnelWindow;
};

const trackIdoFunnelEvent = (
  eventName: string,
  payload: FunnelPayload,
  dedupeKey: string
) => {
  if (firedEvents.has(dedupeKey)) {
    return false;
  }

  firedEvents.add(dedupeKey);

  const analyticsWindow = getWindow();
  if (!analyticsWindow) {
    return true;
  }

  if (typeof analyticsWindow.gtag === "function") {
    analyticsWindow.gtag("event", eventName, payload);
  }

  analyticsWindow.dataLayer = analyticsWindow.dataLayer || [];
  analyticsWindow.dataLayer.push({
    event: eventName,
    ...payload,
  });

  return true;
};

export const trackWalletConnectSuccess = ({
  idoId,
  walletAddress,
  chainId,
}: {
  idoId?: string;
  walletAddress?: string;
  chainId?: number | null;
}) => {
  if (!idoId || !walletAddress) {
    return false;
  }

  return trackIdoFunnelEvent(
    "wallet_connect_success",
    {
      ido_id: idoId,
      wallet_address_hash: null,
      chain_id: toNullableNumber(chainId),
      wallet_connected: true,
    },
    `wallet_connect_success:${idoId}:${walletAddress.toLowerCase()}`
  );
};

export const trackSaleSuccess = ({
  idoId,
  transactionId,
  chainId,
  paymentToken,
  contributionAmount,
  valueUsd,
  tokensToReceive,
}: {
  idoId?: string;
  transactionId?: string | null;
  chainId?: number | null;
  paymentToken?: string | null;
  contributionAmount?: number | string | null;
  valueUsd?: number | string | null;
  tokensToReceive?: number | string | null;
}) => {
  if (!idoId || !transactionId) {
    return false;
  }

  const normalizedContributionAmount = toNullableNumber(contributionAmount);

  return trackIdoFunnelEvent(
    "sale_success",
    {
      ido_id: idoId,
      wallet_address_hash: null,
      transaction_id: transactionId,
      chain_id: toNullableNumber(chainId),
      payment_token: normalizePaymentToken(paymentToken),
      contribution_amount: normalizedContributionAmount,
      value_usd: toNullableNumber(valueUsd) ?? normalizedContributionAmount,
      currency: "USD",
      tokens_to_receive: toNullableNumber(tokensToReceive),
      sale_stage: "live_sale",
    },
    `sale_success:${idoId}:${transactionId}`
  );
};

export const resetIdoFunnelTrackingForTests = () => {
  firedEvents.clear();
};
